import {useEffect, useMemo, useState} from 'react';
import {Link, useLocation, useSearchParams} from 'react-router-dom';
import {SoopUiImg} from '@/components/ui';
import {SOOP_UI_IMG_TYPES} from '@/constants/common/images';
import {useBroadcastContext} from '@/components/main/common/Broadcast/BroadcastProvider';
import {isEmpty} from 'lodash';
import {ROUTE} from '@/constants/main/route';
import {AUTH_NO_TYPES, VOD_TYPES} from '@/constants';
import {convertToArray, ensureHttpsUrl} from '@/utils';
import {
  convertToDuration,
  convertViewCnt,
  formatDate,
  getCategoryThumbUrl,
  goLogin,
  isLogin,
} from '@/utils/main';
import {
  sendLegacyInflowPathLog,
  sendLiveThumbnailClickLog,
  sendVodThumbnailClickLog,
} from '@/utils/main/my/log';
import throwToast from '@/utils/ui/toast';
import MyHistoryApi from '@/api/my/history';
import MyPlusApi from '@/api/main/my-plus';
import useGlobalUIStore from '@/stores';
import {DISPLAY_THUMBNAIL} from '@/constants/common/thumbnail';

const THUMBNAIL_TYPES = {
  ADULT: 'ADULT',
  ADULT_LOCK: 'ADULT_LOCK',
  LOCK: 'LOCK',
  NORMAL: 'NORMAL',
  DELETE: 'DELETE',
};

const getDefaultThumbnail = ({isPrivate, isError}) => {
  if (isPrivate) {
    return 'thumb-private';
  }
  if (isError) {
    return 'thumb-default';
  }
  return null;
};

const BroadcastThumbnailMain = ({children}) => {
  const {broadcastType, vodType} = useBroadcastContext();

  if (broadcastType !== 'vod' && broadcastType !== 'live') {
    throw new Error('broadcastType must be "live" or "vod".');
  }

  if (broadcastType === 'vod' && !vodType) {
    throw new Error('vodType is required.');
  }
  return <div className='thumbs-box'>{children}</div>;
};

const VodThumbnail = () => {
  const location = useLocation();
  const {
    broadcastId,
    broadcastLink,
    vodType,
    hasShowLaterButton,
    thumbnailUrl: tempThumbnailUrl,
    previewThumbnailUrl,
    originalUserId,
    originalUserNickname,
    duration,
    lastViewDuration,
    totalDuration,
    authNumber,
    categoryNo,
    vodCategoryNo,
    accessibleAge,
    hasBadge,
    allowsAdult,
    isPpv,
    userId,
    hashTags,
    thumbnailType,
    categoryTags,
    isUserClip,
    log,
    isTitleHover,
    handleAfterLogSend,
  } = useBroadcastContext();
  const {isDarkMode} = useGlobalUIStore();

  const [searchParams] = useSearchParams();
  const searchKeyword = searchParams.get('szKeyword');
  const [isMouseHovering, setMouseHovering] = useState(false);
  const [imageError, setImageError] = useState(false);

  const isPrivate =
    Number(authNumber) === AUTH_NO_TYPES.PRIVATE ||
    thumbnailType === THUMBNAIL_TYPES.LOCK;

  const thumbnailUrl = ensureHttpsUrl(tempThumbnailUrl);
  const thumbnailImgUrl = useMemo(() => {
    if (allowsAdult) {
      return getCategoryThumbUrl(
        vodCategoryNo,
        DISPLAY_THUMBNAIL.HORIZONTAL,
        'vod',
        isDarkMode,
      );
    } else {
      return thumbnailUrl;
    }
  }, [allowsAdult, thumbnailUrl, isDarkMode, vodCategoryNo]);

  const shouldShowPreview = useMemo(() => {
    // 다시보기, webp(미리 보기)파일없을때, 19금, 유로, 구독 전용일때
    return !(
      vodType === VOD_TYPES.REVIEW ||
      !previewThumbnailUrl ||
      allowsAdult ||
      isPpv ||
      Number(authNumber) === Number(AUTH_NO_TYPES.READ_SUBSCRIPTION)
    );
  }, [vodType, isPpv, previewThumbnailUrl, allowsAdult, authNumber]);

  const handleThumbnailClick = () => {
    const currentPathname = location.pathname;

    if (currentPathname === ROUTE.MY.HISTORY.LATER) {
      MyHistoryApi.viewLaterVod(broadcastId).then(() => {});
    }
    // VOD 썸네일 클릭 로그 전송
    sendVodThumbnailClickLog({
      streamerId: originalUserId ? originalUserId : userId, // 원저작자가 있는 영상일 경우에는 bj 가 원저작자 아이디로, 없는 경우에는 업로드한 유저 아이디로
      vodNumber: broadcastId,
      fileType: vodType,
    });

    // 기존 유입 로그 전송
    sendLegacyInflowPathLog({
      broadcastId: broadcastId,
      streamerId: originalUserId ? originalUserId : userId, // 원저작자가 있는 영상일 경우에는 bj 가 원저작자 아이디로, 없는 경우에는 업로드한 유저 아이디로
      type: 'vod',
      moreData: {
        contentsType: 'vod',
        hashTags: convertToArray(hashTags),
        categoryTags: convertToArray(categoryTags),
        exCategoryNo: broadcastId,
        isUserClip,
        ...(searchKeyword && {skey: searchKeyword}),
        ...(log?.groupName && {groupName: log.groupName}),
        ...(log?.groupType && {groupType: log.groupType}),
        ...(log?.listDataType && {listDataType: log.listDataType}),
      },
      isLogSendData: {
        uploader: userId,
        ...log?.data,
      },
    });

    handleAfterLogSend?.();
  };

  const handleMouseEnter = () => {
    if (shouldShowPreview) {
      setMouseHovering(true);
    }
  };

  useEffect(() => {
    if (isTitleHover) {
      handleMouseEnter();
    } else {
      setMouseHovering(false);
    }
  }, [isTitleHover]);

  // 썸네일 이미지, 비공개, 19금 일반
  return (
    <div
      className='thumbs-box'
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setMouseHovering(false)}>
      <Link
        className={getDefaultThumbnail({
          isPrivate,
          isError: imageError,
        })}
        to={broadcastLink}
        target='_blank'
        onClick={handleThumbnailClick}
        data-url={thumbnailImgUrl}>
        <SoopUiImg
          type={SOOP_UI_IMG_TYPES.thumbnail}
          src={isMouseHovering ? previewThumbnailUrl : thumbnailImgUrl}
          isPrivate={isPrivate}
          onError={() => setImageError(true)}
        />
        {/* Catch 제외 VOD 시청 구간 표시 */}
        {lastViewDuration > 0 &&
          totalDuration > 0 &&
          vodType !== VOD_TYPES.CATCH && (
            <span className='last-view-progress'>
              <em
                className='last-view-progress-bar'
                style={{
                  width: `${Math.max(7, (lastViewDuration / totalDuration) * 100).toFixed(2)}%`,
                }}></em>
            </span>
          )}
      </Link>
      {hasBadge && (
        <div className='overlay-badge' data-ppv={isPpv}>
          {!isPpv && !shouldShowPreview && (
            <span className='nonView'>미리보기 미지원</span>
          )}
          {vodType === VOD_TYPES.REVIEW ? (
            <>
              <span className='_replay'>다시보기</span>
              {Number(authNumber) === AUTH_NO_TYPES.READ_SUBSCRIPTION && (
                <em className='_subscribe plus'>
                  <span>구독</span>
                </em>
              )}
            </>
          ) : (
            <>
              {Number(authNumber) === AUTH_NO_TYPES.READ_SUBSCRIPTION && (
                <em className='_subscribe'>
                  <span>구독</span>
                </em>
              )}
            </>
          )}
          {vodType === VOD_TYPES.HIGHLIGHT && (
            <span className='_highlight'>하이라이트</span>
          )}
          {vodType === VOD_TYPES.CATCH_STORY && (
            <span data-testid='story' className='story'>
              스토리
            </span>
          )}
          {originalUserNickname &&
            originalUserId &&
            vodType !== VOD_TYPES.CATCH &&
            vodType !== VOD_TYPES.CATCH_STORY && (
              <span className='clip_nick'>{originalUserNickname}</span>
            )}
          {/*
          유료 vod인 경우 호버 시 유료 뱃지와 미리보기 뱃지가 동시 노출되어 좁은 해상도에서 뱃지가 깨짐
          따라서 유료 vod인 경우 미리보기 뱃지 노출되지 않도록 조건 추가
          */}
          {isPpv && <span className='ppv'>유료</span>}
        </div>
      )}
      {allowsAdult && <span className='status adult'>연령제한</span>}
      {/* api 응답값마다 duration 형식이 다다르다. */}
      {/* isEmpty 숫자일 경우 비어있다고 인식함 */}
      {!isEmpty(duration) && vodType !== VOD_TYPES.CATCH && (
        <span data-testid='duration' className='time active'>
          {convertToDuration(duration)}
        </span>
      )}
      {vodType === VOD_TYPES.CATCH && (
        <span data-testid='catch-badge' className='catch'></span>
      )}
    </div>
  );
};

const LiveThumbnail = () => {
  const {
    liveType,
    broadcastLink,
    thumbnailUrl,
    viewCount,
    startDateTime,
    hasPassword,
    canResend,
    allowsAdult,
    userId,
    hashTags,
    categoryTags,
    categoryNo,
    categoryName,
    log,
    broadcastId,
    broadBps,
    broadResolution,
    rankIndex,
    handleAfterLogSend,
    subscriptionOnly,
  } = useBroadcastContext();
  const {isDarkMode} = useGlobalUIStore();

  const [imageError, setImageError] = useState(false);

  const thumbnailImgUrl = useMemo(() => {
    if (allowsAdult || hasPassword) {
      return getCategoryThumbUrl(
        categoryNo,
        DISPLAY_THUMBNAIL.HORIZONTAL,
        'live',
        isDarkMode,
      );
    } else {
      return thumbnailUrl;
    }
  }, [allowsAdult, hasPassword, thumbnailUrl, isDarkMode, categoryNo]);

  const formattedStartDate = useMemo(
    () => formatDate(startDateTime, 'MM-dd HH:mm'),
    [startDateTime],
  );

  const handleThumbnailClick = () => {
    //방송 썸네일 클릭 로그
    sendLiveThumbnailClickLog({
      streamerId: userId,
      broadcastId,
      broadCateNo: categoryNo,
      broadBps,
      broadResolution,
    });

    // 기존에 사용하던 라이브 방송 유입 로그
    sendLegacyInflowPathLog({
      broadcastId,
      streamerId: userId,
      moreData: {
        hashTags: convertToArray(hashTags),
        categoryTags: convertToArray(categoryTags),
        contentsType: 'live',
        broadCateNo: categoryNo,
        directoryCateId: categoryName,
        ...(log?.listDataType && {listDataType: log.listDataType}),
      },
      isLogSendData: log?.data,
    });

    handleAfterLogSend?.();
  };

  return (
    <div className='thumbs-box'>
      <Link
        className={getDefaultThumbnail({
          isPrivate: false,
          isError: imageError,
        })}
        to={broadcastLink}
        target='_blank'
        onClick={handleThumbnailClick}
        data-url={thumbnailImgUrl}>
        <SoopUiImg
          type={SOOP_UI_IMG_TYPES.thumbnail}
          src={thumbnailImgUrl}
          onError={() => setImageError(true)}
        />
      </Link>
      {Number(rankIndex) > 0 && <span className='rank'>{rankIndex}</span>}
      {Boolean(Number(canResend)) && <span className='allow'>탐방</span>}
      <span className='views'>
        <em data-testid='view-count'>{convertViewCnt(viewCount)}</em>
        {Number(liveType) === 40 && <span className='ppv'>유료</span>}
        {(Number(subscriptionOnly) === 1 || Number(subscriptionOnly) === 2) && (
          <em className='_subscribe plus'>
            <span>구독</span>
          </em>
        )}
      </span>
      {allowsAdult && hasPassword ? (
        <span className='status adult_lock'>연령제한+비밀번호</span>
      ) : allowsAdult ? (
        <span className='status adult'>연령제한</span>
      ) : hasPassword ? (
        <span className='status lock'>비밀번호</span>
      ) : null}
      <span data-testid='live-start-time' className='time'>
        {formattedStartDate} 방송시작
      </span>
    </div>
  );
};

const PlayListThumbnail = () => {
  const [imageClassName, setImageClassName] = useState('');

  const {
    broadcastLink,
    thumbnailType,
    thumbnailUrl,
    accessibleAge,
    categoryNo,
    vodCategoryNo,
    allowsAdult,
    hasPassword,
    playlistCount,
    playlistId,
    vodOriginalId,
    vodUploaderId,
    broadcastId,
    log,
    handleAfterLogSend,
  } = useBroadcastContext();
  const {isDarkMode} = useGlobalUIStore();

  const isPrivate = hasPassword || thumbnailType === THUMBNAIL_TYPES.LOCK;
  const isAdultContent =
    allowsAdult ||
    (Number(accessibleAge) === 19 && Number(categoryNo) === 30000) ||
    thumbnailType === THUMBNAIL_TYPES.ADULT;
  const isAdultAndPrivate =
    (allowsAdult && hasPassword) ||
    thumbnailType === THUMBNAIL_TYPES.ADULT_LOCK;

  /* 재생목록에서는 비공개 썸네일이 안나오지만 우선 코드가 있길래 유지함... */
  const shouldShowImage = !isPrivate && !isAdultAndPrivate;

  const thumbnailImgUrl = useMemo(() => {
    if (isAdultContent) {
      return getCategoryThumbUrl(
        vodCategoryNo,
        DISPLAY_THUMBNAIL.HORIZONTAL,
        'vod',
        isDarkMode,
      );
    } else {
      return thumbnailUrl;
    }
  }, [isAdultContent, thumbnailUrl, isDarkMode, vodCategoryNo]);

  useEffect(() => {
    if (
      isEmpty(thumbnailImgUrl) ||
      playlistCount === 0 ||
      thumbnailType === THUMBNAIL_TYPES.DELETE
    ) {
      setImageClassName('thumb-default');
    }
  }, [thumbnailImgUrl, playlistCount, thumbnailType]);

  const handleImageError = () => {
    if (imageClassName !== 'thumb-default') {
      setImageClassName('thumb-default');
    }
  };

  const handleThumbnailClick = () => {
    if (playlistCount === 0) {
      throwToast('재생 가능한 VOD가 없습니다.');
      return;
    }

    if (playlistId && vodOriginalId && vodUploaderId) {
      sendLegacyInflowPathLog({
        broadcastId: broadcastId,
        streamerId: vodOriginalId,
        type: 'vod',
        isLogSendData: {...log.data, uploader: vodUploaderId},
      });
    }

    if (handleAfterLogSend) {
      handleAfterLogSend();
    }
  };

  return (
    <div className='thumbs-box'>
      {(isPrivate || isAdultAndPrivate) && (
        <>
          <Link
            to={broadcastLink}
            target='_blank'
            onClick={handleThumbnailClick}
            className={getDefaultThumbnail({
              isPrivate: true,
            })}
            data-url={thumbnailImgUrl}>
            <SoopUiImg
              src={thumbnailImgUrl}
              isPrivate={true}
              onError={handleImageError}
            />
            <div className='play_list'>
              <em>{playlistCount}</em>
            </div>
            {isAdultContent && <span className='status adult'>연령제한</span>}
            비공개
          </Link>
        </>
      )}
      {shouldShowImage && (
        <Link
          className={imageClassName}
          to={broadcastLink}
          target='_blank'
          onClick={handleThumbnailClick}
          rel='noopener noreferrer'>
          {imageClassName !== 'thumb-default' && (
            <SoopUiImg src={thumbnailImgUrl} onError={handleImageError} />
          )}
          <div className='play_list'>
            <em>{playlistCount}</em>
          </div>
          {isAdultContent && <span className='status adult'>연령제한</span>}
        </Link>
      )}
    </div>
  );
};

const ShowLaterButton = () => {
  const {broadcastType, broadcastId} = useBroadcastContext();

  const handleViewingLaterButtonClicked = async () => {
    if (isLogin()) {
      try {
        const response = await MyPlusApi.setLaterView(
          (broadcastType || '').toUpperCase() === 'VOD' ? 'VOD' : 'LIVE',
          broadcastId,
        );
        if (response) {
          throwToast(response.message);
        }
      } catch (error) {
        const {message} = error.response.data;
        throwToast(message);
      }
    } else {
      //비로그인시에는 로그인페이지로 이동
      goLogin();
    }
  };

  return (
    <button
      type='button'
      className='later'
      tip='나중에 보기'
      onClick={handleViewingLaterButtonClicked}>
      <span>나중에 보기</span>
    </button>
  );
};

const BroadcastThumbnail = Object.assign(BroadcastThumbnailMain, {
  VodThumbnail,
  LiveThumbnail,
  PlayListThumbnail,
  ShowLaterButton,
});

export default BroadcastThumbnail;
